import { useEffect, useState } from "react";
import {
  type AbandonedCapturesStore,
  type AbandonedEntry,
  CAPTURES_ABANDONED_EVENT,
  createAbandonedCapturesStore,
} from "../lib/captures/abandoned-captures";
import { captureDisplayTitle, captureHasOwnTitle } from "../lib/captures/labels";
import { capacitorPreferencesAdapter } from "../lib/captures/preferences-adapter";
import { Button } from "./ui/Button";

// Notice usager des captures ABANDONNÉES par le filet de re-synchro (audio
// local définitivement introuvable — finding QA resync #3, formulation validée
// PO 15-07). Affichée sur l'accueil tant que des abandons n'ont pas été VUS ;
// « OK » les marque vus (ils restent au journal, plafonné, pour trace). La
// bannière écoute l'événement émis par le filet : un abandon détecté au resync
// du montage apparaît sans attendre le prochain écran.

function frDateLong(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

interface AbandonedNoticeProps {
  /** Store injectable (tests) ; défaut = journal Preferences de l'appareil. */
  store?: AbandonedCapturesStore;
}

export function AbandonedNotice({ store }: AbandonedNoticeProps) {
  const [resolved] = useState(
    () => store ?? createAbandonedCapturesStore(capacitorPreferencesAdapter),
  );
  const [entries, setEntries] = useState<AbandonedEntry[]>([]);

  useEffect(() => {
    let alive = true;
    const refresh = () =>
      void resolved.unseen().then((e) => {
        if (alive) setEntries(e);
      });
    refresh();
    window.addEventListener(CAPTURES_ABANDONED_EVENT, refresh);
    return () => {
      alive = false;
      window.removeEventListener(CAPTURES_ABANDONED_EVENT, refresh);
    };
  }, [resolved]);

  if (entries.length === 0) return null;

  const plural = entries.length > 1;
  const message = plural
    ? `${entries.length} captures n'ont pas pu être envoyées : leur enregistrement audio est introuvable sur cet appareil. Elles ont été retirées de la file d'envoi.`
    : "Une capture n'a pas pu être envoyée : son enregistrement audio est introuvable sur cet appareil. Elle a été retirée de la file d'envoi.";

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-r-md border-l-2 border-amber-400 bg-amber-50 px-3 py-3 text-small text-ink"
    >
      <p className="m-0">{message}</p>
      <ul className="m-0 list-none p-0 text-muted">
        {entries.map((e) => (
          <li key={e.capture.id}>
            {captureDisplayTitle(e.capture)}
            {/* Un abandon est souvent sans titre : le titre par défaut porte
                déjà la date, on ne l'ajoute que si un vrai titre la masque. */}
            {captureHasOwnTitle(e.capture) && ` — ${frDateLong(e.capture.recordedAt)}`}
          </li>
        ))}
      </ul>
      <Button
        variant="ghost"
        size="sm"
        className="self-end"
        onClick={() => {
          void resolved.markAllSeen(new Date().toISOString()).then(() => setEntries([]));
        }}
      >
        OK
      </Button>
    </div>
  );
}
